import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  TableCell,
  TableRow,
  TextField,
} from "@mui/material";
import type React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import type { OperationsTeam } from "../../types/operationsTeam";
import { formatOrderLocations } from "../../types/order";
import type { PaginationParam, PaginationResponse } from "../../types/paginationResponse";
import {
  type SuratJalanListItem,
  type SuratJalanSearchFilters,
  SuratJalanStatus,
} from "../../types/suratJalan";
import { getOrderStatusAccentColor } from "../../utils/orderStatusAccent";
import DetailSectionCard from "../common/DetailSectionCard";
import OrderStatusChip from "../common/OrderStatusChip";
import DataTable from "../common/Table";
import type { User } from "../../types/user";
import { hasSuratJalanRowActions } from "../../utils/suratJalanActions";
import SuratJalanRowActions from "./SuratJalanRowActions";

type TranslationKeys = keyof typeof translations.en;

interface SuratJalanTableProps {
  rows: SuratJalanListItem[];
  teams: OperationsTeam[];
  filters: SuratJalanSearchFilters;
  loading?: boolean;
  pagination?: PaginationResponse | null;
  hideStatusFilter?: boolean;
  onFiltersChange: (filters: SuratJalanSearchFilters) => void;
  onSearch: (filters: SuratJalanSearchFilters, pagination: PaginationParam) => Promise<void>;
  onPaginationChange?: (pagination: PaginationParam) => void;
  user?: User | null;
  onActionComplete?: () => void;
}

const filterButtonSx = {
  textTransform: "none" as const,
  fontWeight: 600,
  borderRadius: 1.5,
};

const SuratJalanTable: React.FC<SuratJalanTableProps> = ({
  rows,
  teams,
  filters,
  loading,
  pagination,
  hideStatusFilter = false,
  onFiltersChange,
  onSearch,
  onPaginationChange,
  user = null,
  onActionComplete,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const statusOptions = Object.values(SuratJalanStatus).map((status) => ({
    value: status,
    label: t(status as TranslationKeys),
  }));
  const teamOptions = teams.map((team) => ({ value: team._id, label: team.name }));
  const hasActiveFilters = Boolean(filters.status || filters.teamOperationsId);

  const showActionsColumn = hasSuratJalanRowActions(user);

  const columns = [
    { id: "readableId", label: "ID" },
    { id: "order", label: "Orders" },
    { id: "externalOrderId", label: "No. LT/POD" },
    { id: "customer", label: "Customer" },
    { id: "route", label: "Route" },
    { id: "departure", label: "Departure" },
    { id: "team", label: "Team" },
    { id: "status", label: "Status" },
    ...(showActionsColumn ? [{ id: "actions", label: "Actions" }] : []),
  ];

  return (
    <>
      <DetailSectionCard
        title={t("reports.filtersSection" as TranslationKeys)}
        icon={<FilterListOutlinedIcon fontSize="small" />}
        iconColor="#64748B"
        sx={{ mb: 2 }}
      >
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: hideStatusFilter ? "1fr" : "1fr 1fr" },
          }}
        >
          {!hideStatusFilter && (
            <FormControl fullWidth size="small">
              <Autocomplete
                value={statusOptions.find((option) => option.value === filters.status) || null}
                onChange={(_, newValue) =>
                  onFiltersChange({ ...filters, status: newValue?.value || "" })
                }
                options={[{ value: "", label: t("All" as TranslationKeys) }, ...statusOptions]}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("Status" as TranslationKeys)}
                    inputProps={{
                      ...params.inputProps,
                      "data-testid": TestId.SURAT_JALAN.STATUS_FILTER,
                    }}
                  />
                )}
                size="small"
              />
            </FormControl>
          )}
          <FormControl fullWidth size="small">
            <Autocomplete
              value={
                teamOptions.find((option) => option.value === filters.teamOperationsId) || null
              }
              onChange={(_, newValue) =>
                onFiltersChange({ ...filters, teamOperationsId: newValue?.value || "" })
              }
              options={[{ value: "", label: t("All" as TranslationKeys) }, ...teamOptions]}
              getOptionLabel={(option) =>
                option.value === "" ? t("All" as TranslationKeys) : option.label
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Team" as TranslationKeys)}
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": TestId.SURAT_JALAN.TEAM_FILTER,
                  }}
                />
              )}
              size="small"
            />
          </FormControl>
        </Box>
        <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => onSearch(filters, { page: 1, pageSize: pagination?.pageSize || 10 })}
            sx={filterButtonSx}
          >
            {t("Search" as TranslationKeys)}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              sx={filterButtonSx}
              onClick={() => {
                const cleared = { status: "", teamOperationsId: "" };
                onFiltersChange(cleared);
                void onSearch(cleared, { page: 1, pageSize: pagination?.pageSize || 10 });
              }}
            >
              {t("reports.clearFilters" as TranslationKeys)}
            </Button>
          )}
        </Box>
      </DetailSectionCard>

      <DataTable
        data={rows}
        columns={columns.map((column) => ({
          ...column,
          label: t(column.label as TranslationKeys),
          sortable: false,
        }))}
        loading={loading}
        showSearch={false}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        testId={TestId.SURAT_JALAN.TABLE}
        renderRow={(row) => {
          const route = [
            formatOrderLocations(row.order?.load),
            formatOrderLocations(row.order?.unload),
          ]
            .filter((part) => part && part !== "—")
            .join(" → ");
          return (
            <TableRow
              key={row._id}
              hover
              onClick={() => navigate(`/surat-jalan/${row.readableId}`)}
              sx={{
                cursor: "pointer",
                borderLeft: `4px solid ${getOrderStatusAccentColor(row.status)}`,
              }}
            >
              <TableCell>{row.readableId}</TableCell>
              <TableCell>{row.order?.readableId || "—"}</TableCell>
              <TableCell>{row.order?.externalOrderId || "—"}</TableCell>
              <TableCell>{row.order?.customer?.name || "—"}</TableCell>
              <TableCell>{route || "—"}</TableCell>
              <TableCell>{row.order?.departureTimestamp?.slice(0, 10) || "—"}</TableCell>
              <TableCell>{row.order?.teamOperations?.name || "—"}</TableCell>
              <TableCell>
                <OrderStatusChip status={row.status} />
              </TableCell>
              {showActionsColumn && onActionComplete && (
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <SuratJalanRowActions row={row} user={user} onActionComplete={onActionComplete} />
                </TableCell>
              )}
            </TableRow>
          );
        }}
      />
    </>
  );
};

export default SuratJalanTable;
